import { AUDIT_EVENT_KINDS } from 'src/constants/auditLog';

/**
 * Audit entries written by the backend carry a `system` / `system-<source>` marker
 * (e.g. `system-camber`) as their actor instead of a user id. There is no `users`
 * document behind them, so they must not be looked up as a user.
 *
 * @param {*} userId
 * @return {boolean}
 */
export const isSystemActor = (userId) => typeof userId === 'string' && userId.startsWith('system');

/**
 * A custom audit trail event that a feature adds to AUDIT_TRAIL_EVENTS in AuditLogMixin.
 *
 * @typedef {Object} AuditTrailEvent
 * @property {string} kind - Distinct from every AUDIT_EVENT_KINDS value.
 * @property {string} collection
 * @property {string} field
 * @property {() => string} label
 * @property {(auditLogEvent: Object) => string} describe
 * @property {(value: *) => string} formatValue
 */

/**
 * @param {{ collection: string, field?: string }} auditLogEvent
 * @param {AuditTrailEvent[]} trailEvents
 * @return {AuditTrailEvent|undefined}
 */
export const findAuditTrailEvent = ({ collection, field }, trailEvents) => trailEvents.find((trailEvent) => (
  trailEvent.collection === collection && trailEvent.field === field
));

/**
 * @param {{ collection: string, field?: string, resourceAdded?: boolean, resourceDeleted?: boolean,
 *   resourceRecovered?: boolean }} auditLogEvent
 * @param {AuditTrailEvent[]} [trailEvents]
 * @return {string} One of AUDIT_EVENT_KINDS, or a trail event's kind.
 */
export function toAuditEventKind(auditLogEvent, trailEvents = []) {
  const { resourceAdded, resourceDeleted, resourceRecovered } = auditLogEvent;
  const trailEvent = findAuditTrailEvent(auditLogEvent, trailEvents);

  if (trailEvent) {
    return trailEvent.kind;
  }

  if (resourceAdded) {
    return AUDIT_EVENT_KINDS.ADDED;
  }

  if (resourceDeleted) {
    return AUDIT_EVENT_KINDS.DELETED;
  }

  if (resourceRecovered) {
    return AUDIT_EVENT_KINDS.RECOVERED;
  }

  return AUDIT_EVENT_KINDS.UPDATED;
} // toAuditEventKind()
